import { useState, useCallback } from 'react';
import { clampPosition, clampSize, getBounds } from '../utils/calculations';

export function useElementInteraction({ 
  initialPosition,
  initialSize,
  onMove,
  onResize,
  containerRef,
  elementType, // Add the element type parameter
  aspectRatio = null // Add aspect ratio parameter for image elements
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [elementStart, setElementStart] = useState({ x: 0, y: 0 });
  const [sizeStart, setSizeStart] = useState({ width: 0, height: 0 });

  const handleDragStart = useCallback((e) => {
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setElementStart(initialPosition);
  }, [initialPosition]);

  const handleResizeStart = useCallback((e) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setSizeStart(initialSize);
  }, [initialSize]);

  const handleMouseMove = useCallback((e) => {
    // Pass element type to getBounds function
    const bounds = getBounds(containerRef, sizeStart, elementType);

    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      
      const newPosition = {
        x: elementStart.x + dx,
        y: elementStart.y + dy
      };
      
      // Pass element type to clampPosition function
      onMove(clampPosition(newPosition, bounds, elementType));
    } else if (isResizing) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      
      const newSize = {
        width: Math.max(20, sizeStart.width + dx),
        height: Math.max(20, sizeStart.height + dy)
      };
      
      // Disable aspect ratio constraint for now to fix glitching
      const clampedSize = clampSize(newSize, { width: 20, height: 20 }, null, sizeStart);
      onResize(clampedSize);
    }
  }, [
    isDragging, 
    isResizing, 
    dragStart, 
    elementStart, 
    sizeStart, 
    onMove, 
    onResize, 
    containerRef, 
    aspectRatio,
    elementType
  ]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  return {
    isDragging,
    isResizing,
    handleDragStart,
    handleResizeStart,
    handleMouseMove,
    handleMouseUp
  };
}